import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, GraduationCap, Briefcase, DollarSign, BookOpen, Building2, Mail, FileText, Send, Copy, CheckCircle2, Loader2, Search, ChevronDown, ChevronUp, Eye, Play, UserCheck, ArrowLeftRight, PenTool } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import ContentManager from '../components/ContentManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [letters, setLetters] = useState([]);
  const [partnerApps, setPartnerApps] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Letter generation state
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [viewingLetter, setViewingLetter] = useState(null);
  const [copied, setCopied] = useState(false);
  const [searchCandidates, setSearchCandidates] = useState('');
  const [searchJobs, setSearchJobs] = useState('');
  const [actioningPartner, setActioningPartner] = useState(null);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();

  const viewAs = async (role) => {
    setSwitching(true);
    try {
      await axios.post(`${API}/admin/impersonate`, { role }, { withCredentials: true });
      window.location.href = role === 'student' ? '/dashboard' : '/employer';
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to switch view');
    }
    setSwitching(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, t, c, j, l, pa] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }).then(r => r.data).catch(() => null),
        axios.get(`${API}/admin/users`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/transactions`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/candidates`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/jobs`, { withCredentials: true }).then(r => r.data?.jobs || []).catch(() => []),
        axios.get(`${API}/admin/letters`, { withCredentials: true }).then(r => r.data).catch(() => []),
        axios.get(`${API}/admin/partners`, { withCredentials: true }).then(r => r.data).catch(() => [])
      ]);
      setStats(s);
      setUsers(u);
      setTransactions(t);
      setCandidates(c);
      setJobs(j);
      setLetters(l);
      setPartnerApps(pa);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateLetter = async () => {
    if (!selectedCandidate || !selectedJob) return;
    setGeneratingLetter(true);
    setGeneratedLetter(null);
    try {
      const { data } = await axios.post(`${API}/admin/generate-letter`, {
        candidate_email: selectedCandidate.email,
        job_id: selectedJob.id,
        employer_company: selectedJob.company,
        additional_notes: additionalNotes
      }, { withCredentials: true });
      setGeneratedLetter(data);
      // Refresh letters
      const lettersRes = await axios.get(`${API}/admin/letters`, { withCredentials: true });
      setLetters(lettersRes.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to generate letter');
    }
    setGeneratingLetter(false);
  };

  const copyLetter = (text) => {
    try { navigator.clipboard.writeText(text); } catch { const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); document.body.removeChild(t); }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePartnerAction = async (appId, action) => {
    setActioningPartner(appId);
    try {
      await axios.post(`${API}/admin/partners/action`, { application_id: appId, action }, { withCredentials: true });
      setPartnerApps(prev => prev.map(a => a.id === appId ? { ...a, status: action === 'approve' ? 'approved' : 'rejected' } : a));
    } catch {}
    setActioningPartner(null);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-zinc-200">Loading admin panel...</div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'content', label: 'Edit Content', icon: PenTool },
    { id: 'candidates', label: 'Candidate Letters', icon: FileText },
    { id: 'letters', label: 'Sent Letters', icon: Mail },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'partners', label: 'Partner Apps', icon: Building2 },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
  ];

  const filteredCandidates = candidates.filter(c =>
    !searchCandidates ||
    c.name?.toLowerCase().includes(searchCandidates.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchCandidates.toLowerCase()) ||
    c.acquired_skills?.some(s => s.toLowerCase().includes(searchCandidates.toLowerCase()))
  );

  const filteredJobs = jobs.filter(j =>
    !searchJobs ||
    j.title?.toLowerCase().includes(searchJobs.toLowerCase()) ||
    j.company?.toLowerCase().includes(searchJobs.toLowerCase())
  );

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xl font-mono font-bold uppercase tracking-[0.2em] text-[#F59E0B] mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Admin Panel</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Institute of Analytics USA&trade; Dashboard
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => viewAs('student')}
              disabled={switching}
              data-testid="view-as-student"
              className="border-2 border-[#0077B6] text-[#0077B6] px-5 py-2.5 text-sm font-medium hover:-translate-y-1 hover:shadow-[4px_4px_0px_#CC0000] transition-all duration-150 flex items-center gap-2 disabled:opacity-50"
            >
              <GraduationCap className="w-4 h-4" strokeWidth={1.5} /> View as Student
            </button>
            <button
              onClick={() => viewAs('employer')}
              disabled={switching}
              data-testid="view-as-employer"
              className="border-2 border-[#CC0000] text-[#F59E0B] px-5 py-2.5 text-sm font-medium hover:-translate-y-1 hover:shadow-[4px_4px_0px_#0077B6] transition-all duration-150 flex items-center gap-2 disabled:opacity-50"
            >
              <Building2 className="w-4 h-4" strokeWidth={1.5} /> View as Employer
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#262626] mb-8 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`admin-tab-${tab.id}`}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-[#002FA7] text-[#0077B6]' 
                  : 'border-transparent text-zinc-200 hover:text-zinc-400'
              }`}
            >
              <tab.icon className="w-4 h-4" strokeWidth={1.5} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border border-[#262626] mb-8">
              {[
                { label: 'Total Users', value: stats.total_users, icon: Users },
                { label: 'Students', value: stats.students, icon: GraduationCap },
                { label: 'Employers', value: stats.employers, icon: Building2 },
                { label: 'Enrollments', value: stats.active_enrollments, icon: BookOpen },
                { label: 'Revenue', value: `$${stats.total_revenue?.toLocaleString() || 0}`, icon: DollarSign },
                { label: 'Jobs Posted', value: stats.jobs_posted, icon: Briefcase },
              ].map((stat, i) => (
                <div key={i} className="p-5 border-r border-b border-[#262626]">
                  <stat.icon className="w-8 h-8 text-[#0077B6] mb-2" strokeWidth={1.5} />
                  <p className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{stat.value}</p>
                  <p className="text-xs font-mono text-zinc-200 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-[#262626]">
              <button
                onClick={() => setActiveTab('candidates')}
                className="p-8 border-r border-[#262626] hover:bg-[#111111] transition-colors text-left group"
              >
                <FileText className="w-14 h-14 text-[#0077B6] mb-3" strokeWidth={1.5} />
                <p className="font-semibold tracking-tight mb-1 group-hover:text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Generate Candidate Letters</p>
                <p className="text-sm text-zinc-200">Match candidates with jobs and generate AI-powered recommendation letters for employers.</p>
              </button>
              <button
                onClick={() => setActiveTab('letters')}
                className="p-8 border-r border-[#262626] hover:bg-[#111111] transition-colors text-left group"
              >
                <Mail className="w-14 h-14 text-[#0077B6] mb-3" strokeWidth={1.5} />
                <p className="font-semibold tracking-tight mb-1 group-hover:text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>View Sent Letters</p>
                <p className="text-sm text-zinc-200">{letters.length} recommendation letter(s) generated. Review and resend to employers.</p>
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className="p-8 border-r border-[#262626] hover:bg-[#111111] transition-colors text-left group"
              >
                <Users className="w-14 h-14 text-[#0077B6] mb-3" strokeWidth={1.5} />
                <p className="font-semibold tracking-tight mb-1 group-hover:text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Manage Users</p>
                <p className="text-sm text-zinc-200">{users.length} total users across students, employers, and admins.</p>
              </button>
              <div className="p-8 hover:bg-[#111111] transition-colors text-left">
                <Play className="w-14 h-14 text-[#F59E0B] mb-3" strokeWidth={1.5} />
                <p className="font-semibold tracking-tight mb-1 text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Preview Course Players</p>
                <div className="space-y-1 mt-3">
                  {["data-engineering", "supervised-algorithm", "deep-learning", "natural-language-processing"].map((s, i) => (
                    <Link key={i} to={`/learn/${s}`} className="block text-sm text-[#0077B6] hover:underline truncate">
                      {s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Link>
                  ))}
                  <Link to="/courses" className="block text-sm text-zinc-400 hover:text-white mt-1">View all courses →</Link>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Content Manager Tab */}
        {activeTab === 'content' && <ContentManager />}

        {/* Candidate Letters Tab */}
        {activeTab === 'candidates' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Candidate Selection */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>1. Select Candidate</h2>
                <div className="flex items-center border border-[#262626] px-2 w-48">
                  <Search className="w-3.5 h-3.5 text-zinc-200" strokeWidth={1.5} />
                  <input
                    data-testid="search-candidates-input"
                    value={searchCandidates}
                    onChange={e => setSearchCandidates(e.target.value)}
                    placeholder="Search..."
                    className="flex-1 px-2 py-1.5 text-sm bg-transparent text-white placeholder-zinc-500 outline-none"
                  />
                </div>
              </div>
              <div className="border border-[#262626] max-h-[340px] overflow-y-auto">
                {filteredCandidates.length === 0 ? (
                  <div className="p-8 text-center text-zinc-200 text-base">
                    <GraduationCap className="w-6 h-6 mx-auto mb-2 text-zinc-300" strokeWidth={1.5} />
                    No candidates found. Students must register first.
                  </div>
                ) : filteredCandidates.map((c, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedCandidate(c)}
                    data-testid={`candidate-select-${i}`}
                    className={`w-full text-left p-4 border-b border-[#262626] last:border-b-0 transition-colors ${
                      selectedCandidate?.email === c.email ? 'bg-[#0077B6]/5 border-l-2 border-l-[#002FA7]' : 'hover:bg-[#111111]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className="font-medium text-base">{c.name}</p>
                      <span className="text-xs font-mono text-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.courses_completed} course(s)</span>
                    </div>
                    <p className="text-xs text-zinc-200 mb-2">{c.email}</p>
                    {c.acquired_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {c.acquired_skills.slice(0, 5).map((s, j) => (
                          <span key={j} className="text-xs font-mono px-1.5 py-0.5 bg-zinc-800 text-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s}</span>
                        ))}
                        {c.acquired_skills.length > 5 && <span className="text-xs text-zinc-200">+{c.acquired_skills.length - 5}</span>}
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Job Selection */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>2. Select Target Job</h2>
                  <div className="flex items-center border border-[#262626] px-2 w-48">
                    <Search className="w-3.5 h-3.5 text-zinc-200" strokeWidth={1.5} />
                    <input
                      data-testid="search-jobs-input"
                      value={searchJobs}
                      onChange={e => setSearchJobs(e.target.value)}
                      placeholder="Search jobs..."
                      className="flex-1 px-2 py-1.5 text-sm bg-transparent text-white placeholder-zinc-500 outline-none"
                    />
                  </div>
                </div>
                <div className="border border-[#262626] max-h-[280px] overflow-y-auto">
                  {filteredJobs.length === 0 ? (
                    <div className="p-8 text-center text-zinc-200 text-base">
                      <Briefcase className="w-6 h-6 mx-auto mb-2 text-zinc-300" strokeWidth={1.5} />
                      No jobs available. Generate jobs from the Job Board first.
                    </div>
                  ) : filteredJobs.map((j, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedJob(j)}
                      data-testid={`job-select-${i}`}
                      className={`w-full text-left p-4 border-b border-[#262626] last:border-b-0 transition-colors ${
                        selectedJob?.id === j.id ? 'bg-[#0077B6]/5 border-l-2 border-l-[#002FA7]' : 'hover:bg-[#111111]'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-base">{j.title}</p>
                        {j.match_score && (
                          <span className="text-xs font-mono text-[#0077B6]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{j.match_score}%</span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-200">{j.company} &middot; {j.location}</p>
                      {j.salary_range && <p className="text-xs text-zinc-300 mt-0.5 font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{j.salary_range}</p>}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Letter Generation */}
            <div>
              <h2 className="text-lg font-semibold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>3. Generate Recommendation Letter</h2>
              
              {/* Selected Summary */}
              <div className="border border-[#262626] p-5 mb-4 space-y-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Candidate</p>
                  {selectedCandidate ? (
                    <p className="text-sm font-medium">{selectedCandidate.name} <span className="text-zinc-200 font-normal">({selectedCandidate.email})</span></p>
                  ) : (
                    <p className="text-sm text-zinc-300 italic">Select a candidate from the left panel</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Target Position</p>
                  {selectedJob ? (
                    <p className="text-sm font-medium">{selectedJob.title} <span className="text-zinc-200 font-normal">at {selectedJob.company}</span></p>
                  ) : (
                    <p className="text-sm text-zinc-300 italic">Select a job from the left panel</p>
                  )}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="mb-4">
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-200 block mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Additional Notes (Optional)</label>
                <textarea
                  data-testid="letter-notes-input"
                  value={additionalNotes}
                  onChange={e => setAdditionalNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none text-base text-white placeholder-zinc-500 resize-none"
                  placeholder="Any specific strengths, achievements, or notes about this candidate..."
                />
              </div>

              <button
                onClick={generateLetter}
                disabled={!selectedCandidate || !selectedJob || generatingLetter}
                data-testid="generate-letter-btn"
                className="brand-btn w-full text-center py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {generatingLetter ? (
                  <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Generating Letter with AI...</>
                ) : (
                  <><FileText className="w-4 h-4" strokeWidth={1.5} /> Generate Recommendation Letter</>
                )}
              </button>

              {/* Generated Letter Preview */}
              {generatedLetter && (
                <div data-testid="generated-letter-preview" className="mt-6 border border-[#262626]">
                  <div className="flex items-center justify-between px-4 py-3 bg-[#111111] border-b border-[#262626]">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" strokeWidth={1.5} />
                      <span className="text-sm font-medium">Letter Generated</span>
                    </div>
                    <button
                      onClick={() => copyLetter(generatedLetter.letter)}
                      data-testid="copy-letter-btn"
                      className="flex items-center gap-1.5 text-xs text-[#0077B6] hover:underline"
                    >
                      {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy to Clipboard</>}
                    </button>
                  </div>
                  <div className="p-5 max-h-[400px] overflow-y-auto">
                    <pre className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                      {generatedLetter.letter}
                    </pre>
                  </div>
                  <div className="px-4 py-3 bg-[#111111] border-t border-[#262626] flex items-center gap-2 text-xs text-zinc-200">
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                    For: {generatedLetter.candidate_name} &rarr; {generatedLetter.job_title} at {generatedLetter.company}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sent Letters Tab */}
        {activeTab === 'letters' && (
          <div>
            <h2 className="text-lg font-semibold tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Generated Recommendation Letters ({letters.length})
            </h2>
            {letters.length === 0 ? (
              <div className="text-center py-16 border border-[#262626]">
                <FileText className="w-16 h-16 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-zinc-300">No letters generated yet</p>
                <p className="text-sm text-zinc-200 mt-1">Go to "Candidate Letters" tab to generate recommendation letters.</p>
              </div>
            ) : (
              <div className="space-y-0 border border-[#262626]">
                {letters.map((letter, i) => (
                  <div key={i} data-testid={`letter-row-${i}`} className="border-b border-[#262626] last:border-b-0">
                    <div className="flex items-center justify-between p-5">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#0077B6]/10 flex items-center justify-center">
                          <FileText className="w-10 h-10 text-[#0077B6]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <p className="font-medium text-base">{letter.candidate_name} &rarr; {letter.job_title}</p>
                          <p className="text-xs text-zinc-200">{letter.company} &middot; {new Date(letter.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyLetter(letter.letter_content)}
                          className="text-xs text-[#0077B6] hover:underline flex items-center gap-1"
                        >
                          <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Copy
                        </button>
                        <button
                          onClick={() => setViewingLetter(viewingLetter === letter.id ? null : letter.id)}
                          className="text-xs text-zinc-300 hover:text-zinc-700 flex items-center gap-1"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} /> {viewingLetter === letter.id ? 'Hide' : 'View'}
                        </button>
                      </div>
                    </div>
                    {viewingLetter === letter.id && (
                      <div className="px-5 pb-5">
                        <div className="bg-[#111111] border border-[#262626] p-5 max-h-[400px] overflow-y-auto">
                          <pre className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" style={{ fontFamily: 'Satoshi, sans-serif' }}>
                            {letter.letter_content}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="border border-[#262626]">
            <div className="grid grid-cols-4 gap-4 p-4 bg-[#111111] border-b border-[#262626] text-xs font-mono uppercase tracking-[0.2em] text-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              <div>Name</div>
              <div>Email</div>
              <div>Role</div>
              <div>Joined</div>
            </div>
            {users.map((u, i) => (
              <div key={i} data-testid={`admin-user-${i}`} className="grid grid-cols-4 gap-4 p-4 border-b border-[#262626] last:border-b-0 text-base">
                <div className="font-medium">{u.name}</div>
                <div className="text-zinc-300 truncate">{u.email}</div>
                <div>
                  <span className={`text-xs font-mono px-2 py-0.5 ${
                    u.role === 'admin' ? 'bg-red-900/30 text-red-400' :
                    u.role === 'employer' ? 'bg-blue-900/30 text-blue-400' :
                    'bg-green-900/30 text-green-400'
                  }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{u.role}</span>
                </div>
                <div className="text-zinc-200">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</div>
              </div>
            ))}
            {users.length === 0 && <div className="p-8 text-center text-zinc-200 text-base">No users found</div>}
          </div>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <div>
            <h2 className="text-lg font-semibold tracking-tight mb-6 text-[#0077B6]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Employer Partnership Applications ({partnerApps.length})
            </h2>
            {partnerApps.length === 0 ? (
              <div className="text-center py-16 border border-[#262626] bg-[#111111]">
                <Building2 className="w-16 h-16 text-zinc-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-zinc-300">No partner applications yet</p>
              </div>
            ) : (
              <div className="border border-[#262626]">
                {partnerApps.map((app, i) => (
                  <div key={i} data-testid={`partner-app-${i}`} className="p-5 border-b border-[#262626] last:border-b-0 bg-[#111111]">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-base font-semibold text-white">{app.company}</p>
                        <p className="text-sm text-zinc-300">{app.contact_name} &middot; {app.email}</p>
                        {app.website && <p className="text-sm text-[#0077B6] mt-0.5">{app.website}</p>}
                        {app.reason && <p className="text-sm text-zinc-400 mt-2 italic">"{app.reason}"</p>}
                        <p className="text-xs text-zinc-500 mt-2">{new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {app.status === 'pending' ? (
                          <>
                            <button
                              onClick={() => handlePartnerAction(app.id, 'approve')}
                              disabled={actioningPartner === app.id}
                              data-testid={`approve-partner-${i}`}
                              className="brand-btn text-sm px-4 py-2 disabled:opacity-50"
                            >
                              {actioningPartner === app.id ? '...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handlePartnerAction(app.id, 'reject')}
                              disabled={actioningPartner === app.id}
                              data-testid={`reject-partner-${i}`}
                              className="brand-btn-red text-sm px-4 py-2 disabled:opacity-50"
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <span className={`text-sm font-mono px-3 py-1 ${
                            app.status === 'approved' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'
                          }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {app.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="border border-[#262626]">
            <div className="grid grid-cols-5 gap-4 p-4 bg-[#111111] border-b border-[#262626] text-xs font-mono uppercase tracking-[0.2em] text-zinc-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              <div>Email</div>
              <div>Course</div>
              <div>Amount</div>
              <div>Status</div>
              <div>Date</div>
            </div>
            {transactions.map((t, i) => (
              <div key={i} data-testid={`admin-txn-${i}`} className="grid grid-cols-5 gap-4 p-4 border-b border-[#262626] last:border-b-0 text-base">
                <div className="text-zinc-300 truncate">{t.user_email}</div>
                <div className="font-medium">{t.course_slug}</div>
                <div className="font-mono" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${t.amount}</div>
                <div>
                  <span className={`text-xs font-mono px-2 py-0.5 ${
                    t.payment_status === 'paid' ? 'bg-green-900/30 text-green-400' :
                    t.payment_status === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                    'bg-zinc-800 text-zinc-200'
                  }`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{t.payment_status}</span>
                </div>
                <div className="text-zinc-200">{t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}</div>
              </div>
            ))}
            {transactions.length === 0 && <div className="p-8 text-center text-zinc-200 text-base">No transactions yet</div>}
          </div>
        )}
      </div>
    </div>
  );
}
